import React, { useEffect } from 'react';
import { makeStyles, Grid, Button } from '@material-ui/core';
import { Typography } from '@swagup-com/components';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { useQueries, useQueryClient, useIsFetching } from 'react-query';
import { setShowDashboardMessage } from '../../actions';
import { HeaderSection, Header } from './SharedComponents';
import { CenteredGrid, Alert } from '../shared';
import Loader from '../global/Loader';
import EmptyState from './EmptyState';
import Orders from './Orders';
import Shipments from './Shipments';
import Proofs from './Proofs';
import styles from './styles/DashboardSummary';
import apiPaths from '../../helpers/apiPaths';
import { useProfile } from '../../hooks';
import { accountOrdersApi, proofsApi } from '../../apis/swagup';

const useStyles = makeStyles(styles);

const eoQueryKey = [apiPaths.employeeOrders, { limit: 4 }];

const OrdersSection = ({ orders, classes }) => (
  <Grid item container lg={8} md={8} sm={12} xs={12} className={classes.separateOrders}>
    <Grid container className={classes.orderContainer} direction="column">
      <Grid item style={{ width: '100%' }}>
        <HeaderSection classes={classes} sectionName="orders" hideViewAll={orders.length === 0} />
      </Grid>
      <Grid container className={clsx(classes.orderGridList, { [classes.orderListES]: orders.length === 0 })}>
        <Grid item xs={12}>
          {orders.length === 0 ? (
            <EmptyState
              sectionName="orders"
              title="No Recent Orders"
              description="You’ve placed no Swag orders recently."
            />
          ) : (
            orders.map((order, index) => <Orders key={order.id} order={order} showLine={index !== 0} />)
          )}
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const ProofsSection = ({ proofs, classes }) => (
  <Grid item container lg={4} md={4} sm={12} xs={12}>
    <Grid container className={classes.proofContainer}>
      <HeaderSection classes={classes} sectionName="proofs" hideViewAll={proofs.length === 0} />
      <Grid item xs={12} className={clsx(classes.proofItem, { [classes.proofItemES]: proofs.length === 0 })}>
        {proofs.length === 0 ? (
          <EmptyState sectionName="proofs" title="No Proofs" description="You’ve submitted no order requests yet." />
        ) : (
          <Proofs proofs={proofs} />
        )}
      </Grid>
    </Grid>
  </Grid>
);

const ShipmentsSection = ({ classes }) => {
  const queryClient = useQueryClient();
  const shipments = queryClient.getQueryData(eoQueryKey)?.results || [];

  return (
    <Grid item container lg={8} md={8} sm={12} xs={12} className={classes.separateShipments}>
      <Grid container className={classes.shipmentsContainer}>
        <Grid item>
          <HeaderSection classes={classes} sectionName="shipments" hideViewAll={shipments.length === 0} />
        </Grid>
        <Grid item>
          <Grid
            container
            className={clsx(classes.shipmentGridList, { [classes.shipmentListES]: shipments.length === 0 })}
          >
            <Grid item xs={12}>
              {shipments.length === 0 ? (
                <EmptyState
                  sectionName="shipments"
                  title="No Recent Shipments"
                  description="You have not sent any Swag recently."
                />
              ) : (
                shipments.map((shipment, index) => (
                  <Shipments key={shipment.id} shipment={shipment} showLine={index !== 3} />
                ))
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ReleasesSection = ({ classes }) => (
  <Grid item container lg={4} md={4} sm={12} xs={12}>
    <Grid container className={classes.releasesContainer}>
      <Grid item md={12}>
        <img src="/images/dashboard/announcement.png" alt="announcement icon" className={classes.releasesImg} />
      </Grid>
      <Grid item md={12} className={classes.releasesContentContainer}>
        <Typography variant="h4BoldInter" style={{ marginBottom: 8 }}>
          What&apos;s New
        </Typography>
        <Typography variant="body2RegularInter" style={{ marginBottom: 16 }}>
          View our latest features and updates on improving your experience.
        </Typography>
        <Button variant="contained" color="secondary" id="beamerButton" style={{ height: 48 }}>
          See Updates
        </Button>
      </Grid>
    </Grid>
  </Grid>
);

const commonFilters = { limit: 4, createdAt: 'last_30_days' };

const DashboardSummary = () => {
  const showMessage = useSelector(state => state.showDashboardMessage);

  const queries = useQueries([
    {
      queryKey: [apiPaths.accountOrders, commonFilters],
      queryFn: () => {
        const query = new URLSearchParams();
        query.set('limit', commonFilters.limit);
        query.set('createdAt', commonFilters.createdAt);
        query.set('ordering', '-created_at');
        return accountOrdersApi.fetchOrders(query.toString());
      }
    },
    {
      queryKey: apiPaths.proofs,
      queryFn: () => {
        const query = new URLSearchParams();
        query.set('ordering', '-created_at');
        return proofsApi.fetch(query.toString());
      }
    }
  ]);

  const { data: profile } = useProfile();
  const { first_name: user } = profile || {};

  const isFetchingShipments = useIsFetching(eoQueryKey);
  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    return () => dispatch(setShowDashboardMessage(false));
  }, [dispatch]);

  const isLoading = isFetchingShipments || queries.some(q => q.status === 'loading');

  if (isLoading) return <Loader />;

  const orders = queries[0]?.data?.results ?? [];
  const proofs = queries[1]?.data?.results ?? [];

  return (
    <div className={classes.main}>
      <Grid container justifyContent="center">
        <CenteredGrid item className={classes.center}>
          <Grid container>
            {showMessage && (
              <Alert style={{ marginBottom: 24 }}>
                <span className={classes.alertText}>Welcome Back, {user} 👋.&nbsp; </span>
              </Alert>
            )}
            <Header classes={classes} />
            <OrdersSection orders={orders} classes={classes} />
            <ProofsSection proofs={proofs} classes={classes} />
            <ShipmentsSection classes={classes} />
            <ReleasesSection classes={classes} />
          </Grid>
        </CenteredGrid>
      </Grid>
    </div>
  );
};

export default DashboardSummary;
